import { format } from 'd3-format';
import { timeFormat } from 'd3-time-format';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
	AreaSeries,
	atr,
	BarSeries,
	bollingerBand,
	BollingerBandTooltip,
	BollingerSeries,
	CandlestickSeries,
	Chart,
	ChartCanvas,
	CrossHairCursor,
	CurrentCoordinate,
	discontinuousTimeScaleProviderBuilder,
	EdgeIndicator,
	elderRay,
	ElderRaySeries,
	ema,
	HoverTooltip,
	InteractiveText,
	Label,
	lastVisibleItemBasedZoomAnchor,
	LineSeries,
	macd,
	MACDSeries,
	MACDTooltip,
	MouseCoordinateX,
	MouseCoordinateY,
	MovingAverageTooltip,
	OHLCSeries,
	OHLCTooltip,
	PriceCoordinate,
	rsi,
	RSISeries,
	RSITooltip,
	SingleValueTooltip,
	sma,
	XAxis,
	YAxis,
	ZoomButtons,
} from 'react-financial-charts';
import dayjs from 'dayjs';
import { isFirefox } from '../../utils/utils';
import { change } from '@react-financial-charts/indicators';
import ModalInteractiveText from './ModalInteractiveText.tsx';
import { saveAs } from 'file-saver';

const ScaleProvider = discontinuousTimeScaleProviderBuilder().inputDateAccessor((d) => {
	const estDateString = new Date(d.date).toLocaleString('en-US', { timeZone: 'America/New_York' });
	// Create a new Date object from the adjusted string
	return new Date(estDateString);
});

const StockChart = (props) => {
	let { initialData, ticker, technical } = props;
	const chartRef = useRef(null); // Reference to the chart for getting dimensions

	const margin = { left: 0, right: 48, top: 45, bottom: 24 };

	const [state, setState] = useState(1);
	const [indicators, setIndicators] = useState([]);
	const [range, setRange] = useState('Daily');
	const [graphType, setGraphType] = useState('Candle');

	const calculateAverage = (number1, number2) => {
		return (Number(number1) + Number(number2)) / 2;
	};
	const hexToRgba = (hex, opacity) => {
		// Remove the '#' if it exists
		hex = hex.replace('#', '');

		// Convert hex to RGB
		let r = parseInt(hex.substring(0, 2), 16);
		let g = parseInt(hex.substring(2, 4), 16);
		let b = parseInt(hex.substring(4, 6), 16);

		// Ensure opacity is within range [0, 1]
		opacity = Math.min(1, Math.max(0, opacity));

		// Return RGBA string
		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	};

	const [zoom, setZoom] = useState(1.1);

	const handleZoomIn = () => setZoom((prevZoom) => prevZoom * 1.2);
	const handleZoomOut = () => setZoom((prevZoom) => prevZoom / 1.2);

	useEffect(() => {
		const socket = new WebSocket(`${import.meta.env.PUBLIC_SOCKET_URL}/ws?ticker=${ticker}`);

		socket.onmessage = (event) => {
			const data = JSON.parse(event.data);
			if (data) {
				if (data.hasOwnProperty('type') && data.type === 'ping') {
					socket.send(JSON.stringify({ type: 'pong' }));
					return;
				}

				document.dispatchEvent(new CustomEvent('PriceUpdate', { detail: data }));
				fetchData(range);
			}
		};

		// Clean up function
		return () => {
			socket.close(); // Close the WebSocket connection
		};
	}, [ticker]);

	initialData = initialData
		.map((item) => {
			const estDateString = new Date(item.date).toLocaleString('en-US', { timeZone: 'America/New_York' });
			// Create a new Date object from the adjusted string
			const estDate = new Date(estDateString);
			return {
				...item,
				date: estDate,
			};
		})
		.sort((a, b) => {
			// Turn your strings into dates, and then subtract them
			// to get a value that is either negative, positive, or zero.
			return new Date(a.date) - new Date(b.date);
		});

	const [graphData, setGraphData] = useState(initialData);
	const maxWidth = 1300;
	const maxHeight = window.innerHeight - 250;

	const initialWidth =
		window.innerWidth <= 1023
			? (window.innerWidth < maxWidth ? window.innerWidth : maxWidth) - margin.left - margin.right
			: (window.innerWidth / 2 < maxWidth ? window.innerWidth / 2 : maxWidth) - margin.left - margin.right;
	const [width, setWidth] = useState(initialWidth);
	let height = Math.floor(width * 0.55) - margin.top - margin.bottom;

	if (height < 350) {
		height = 350;
	} else if (height > maxHeight) {
		height = maxHeight;
	}

	const theme = HSThemeAppearance.getAppearance();

	const fetchData = async (range: string) => {
		const response = await fetch(`${import.meta.env.PUBLIC_API_URL}/stock/${ticker}/chart/${range.toLowerCase()}?limit=1000`);
		if (response.status !== 200) {
			// error
		}
		const resultData = await response.json();
		if (resultData.length === 0) {
			return;
		}

		setGraphData(
			resultData
				.map((item) => {
					const estDateString = new Date(item.date).toLocaleString('en-US', { timeZone: 'America/New_York' });
					// Create a new Date object from the adjusted string
					const estDate = new Date(estDateString);
					return {
						...item,
						date: estDate,
					};
				})
				.sort((a, b) => {
					// Turn your strings into dates, and then subtract them
					// to get a value that is either negative, positive, or zero.
					return new Date(a.date) - new Date(b.date);
				}),
		);
	};

	useEffect(() => {
		if (isFirefox()) {
			setTimeout(() => {
				handleZoomIn();
			}, 1);
		}

		document.addEventListener(
			'GraphRangeChanged',
			(evt) => {
				setRange(evt.detail.range);
				fetchData(evt.detail.range);
			},
			false,
		);

		document.addEventListener(
			'GraphTypeChanged',
			(evt) => {
				setGraphType(evt.detail.type);
			},
			false,
		);

		document.addEventListener(
			'AddIndicator',
			(evt) => {
				indicators.push(evt.detail);
				let newIndicators = Array.from(indicators.reduce((map, obj) => map.set(obj.indicator, obj), new Map()).values());
				setIndicators(newIndicators);
			},
			false,
		);

		window.addEventListener('resize', () => {
			setState((state) => state + 1);
		});
	}, []);

	const { data, xScale, xAccessor, displayXAccessor } = ScaleProvider(graphData);

	const sma50 = sma()
		.id(3)
		.options({ windowSize: 50 })
		.merge((d, c) => {
			d.sma50 = c;
		})
		.accessor((d) => d.sma50)
		.stroke(theme === 'dark' ? '#1d77f6' : '#760eda');

	const ema200 = ema()
		.id(2)
		.options({ windowSize: 200 })
		.merge((d, c) => {
			d.ema200 = c;
		})
		.accessor((d) => d.ema200)
		.stroke(theme === 'dark' ? '#ad890f' : '#c20010');

	const elder = elderRay();
	let calculatedData = elder(sma50(ema200(data)));

	if (graphType === 'OHLC') {
		const calculator = change();

		calculatedData = calculator(calculatedData);
	}

	let tooltipOptions = [
		{
			yAccessor: sma50.accessor(),
			type: 'SMA',
			stroke: sma50.stroke(),
			windowSize: sma50.options().windowSize,
		},
		{
			yAccessor: ema200.accessor(),
			type: 'EMA',
			stroke: ema200.stroke(),
			windowSize: ema200.options().windowSize,
		},
	];

	let atrCustom;
	let smaCustom;
	let emaCustom;
	let macdCustom;
	let rsiCustom;
	let bollingerBandCustom;
	let macdHeight = 0;
	let rsiHeight = 0;
	let tickValues = [];

	indicators.map((obj) => {
		if (obj.indicator === 'atr') {
			atrCustom = atr()
				.id(100)
				.options({ windowSize: Number(obj.window) })
				.merge((d, c) => {
					d.atrCustom = c;
				})
				.accessor((d) => d.atrCustom)
				.stroke(obj.color);
			tooltipOptions.push({
				yAccessor: atrCustom.accessor(),
				type: 'ATR',
				stroke: atrCustom.stroke(),
				windowSize: atrCustom.options().windowSize,
			});
			calculatedData = atrCustom(calculatedData);
		} else if (obj.indicator === 'bb') {
			bollingerBandCustom = bollingerBand()
				.id(101)
				.options({ multiplier: Number(obj.multiplier), movingAverageType: obj.movingAverageType, windowSize: Number(obj.window) })
				.merge((d, c) => {
					d.bb = c;
				})
				.accessor((d) => d.bb)
				.stroke(hexToRgba(obj.color, 0.05));
			calculatedData = bollingerBandCustom(calculatedData);
		} else if (obj.indicator === 'sma') {
			smaCustom = sma()
				.id(102)
				.options({ windowSize: Number(obj.window) })
				.merge((d, c) => {
					d.smaCustom = c;
				})
				.accessor((d) => d.smaCustom)
				.stroke(obj.color);
			tooltipOptions.push({
				yAccessor: smaCustom.accessor(),
				type: 'SMA',
				stroke: smaCustom.stroke(),
				windowSize: smaCustom.options().windowSize,
			});
			calculatedData = smaCustom(calculatedData);
		} else if (obj.indicator === 'ema') {
			emaCustom = ema()
				.id(103)
				.options({ windowSize: Number(obj.window) })
				.merge((d, c) => {
					d.emaCustom = c;
				})
				.accessor((d) => d.emaCustom)
				.stroke(obj.color);
			tooltipOptions.push({
				yAccessor: emaCustom.accessor(),
				type: 'EMA',
				stroke: emaCustom.stroke(),
				windowSize: emaCustom.options().windowSize,
			});
			calculatedData = emaCustom(calculatedData);
		} else if (obj.indicator === 'macd') {
			macdHeight = 100;
			macdCustom = macd()
				.id(104)
				.options({
					fast: Number(obj.fast),
					slow: Number(obj.slow),
					signal: Number(obj.signal),
				})
				.merge((d, c) => {
					d.macdCustom = c;
				})
				.accessor((d) => d.macdCustom)
				.stroke(obj.color);
			calculatedData = macdCustom(calculatedData);
		} else if (obj.indicator === 'rsi') {
			rsiHeight = 100;
			tickValues = [Number(obj.rsiOversold), calculateAverage(obj.rsiOversold, obj.rsiOverbought), Number(obj.rsiOverbought)];
			rsiCustom = rsi()
				.id(105)
				.options({ windowSize: Number(obj.window) })
				.merge((d, c) => {
					d.rsiCustom = c;
				})
				.accessor((d) => d.rsiCustom)
				.stroke(obj.color);
			calculatedData = rsiCustom(calculatedData);
		}
	});

	const pricesDisplayFormat = format('.2f');
	const max = xAccessor(data[data.length - 1]);
	const min = xAccessor(data[Math.max(0, data.length - 100)]);
	const xExtents = [min, max + 5];

	const gridHeight = height - margin.top - margin.bottom;
	height = height + macdHeight + rsiHeight;
	const elderRayHeight = 100;
	const elderRayOrigin = (_, h) => [0, h - elderRayHeight - macdHeight - rsiHeight];
	const barChartHeight = gridHeight / 4;
	const barChartOrigin = (_, h) => [0, h - barChartHeight - macdHeight - rsiHeight - elderRayHeight];
	const chartHeight = gridHeight - elderRayHeight;
	const dateTimeFormat = '%d %b';
	const timeDisplayFormat = timeFormat(dateTimeFormat);

	const barChartExtents = (data) => {
		return data.volume;
	};

	const candleChartExtents = (data) => {
		return [data.high, data.low];
	};

	const yEdgeIndicator = (data) => {
		return data.close;
	};

	const volumeColor = (data) => {
		return data.close > data.open ? 'rgba(38, 166, 154, 0.3)' : 'rgba(239, 83, 80, 0.3)';
	};

	const volumeSeries = (data) => {
		return data.volume;
	};

	const openCloseColor = (data) => {
		return data.close > data.open ? '#26a69a' : '#ef5350';
	};

	const handleDownloadPng = () => {
		const canvasElements = document.querySelectorAll('.finquota-chart canvas');

		if (!canvasElements.length) {
			console.error('No canvas elements found');
			return;
		}

		// Get the dimensions of the first canvas (assuming all have the same size)
		const width = canvasElements[0].width;
		const height = canvasElements[0].height;

		// Create an off-screen canvas with the same dimensions
		const combinedCanvas = document.createElement('canvas');
		combinedCanvas.width = width;
		combinedCanvas.height = height;
		const ctx = combinedCanvas.getContext('2d');

		// Fill the off-screen canvas with a white background
		ctx.fillStyle = 'white';
		ctx.fillRect(0, 0, width, height);

		// Loop over each canvas and draw it onto the off-screen canvas
		canvasElements.forEach((canvas) => {
			ctx.drawImage(canvas, 0, 0, width, height);
		});

		// Add watermark text on the top-right corner
		const watermarkText = 'finquota.com';
		ctx.font = 'bold 40px Arial';
		ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'; // Semi-transparent black text
		ctx.textAlign = 'right'; // Align text to the right
		ctx.textBaseline = 'top'; // Align text to the top of the canvas
		ctx.fillText(watermarkText, width - 20, 20); // Position near the top-right corner with padding

		// Convert the combined canvas to a PNG blob and trigger the download
		combinedCanvas.toBlob((blob) => {
			saveAs(blob, 'finquota_chart.png');
		});
	};

	const formatVolume = (number) => {
		const suffixes = ['', 'K', 'M', 'B', 'T']; // Add more suffixes as needed

		let suffixIndex = 0;

		while (number >= 1000 && suffixIndex < suffixes.length - 1) {
			number /= 1000;
			suffixIndex++;
		}

		return number.toFixed(1) + suffixes[suffixIndex];
	};
	const getTooltipContent = ({ currentItem: d }: { currentItem }) => {
		return {
			x: d.date ? (range === 'Hourly' ? dayjs(d.date).format('MM-DD-YYYY h:mma') : dayjs(d.date).format('MM-DD-YYYY')) : '',
			y: [
				{
					label: 'Volume:',
					value: formatVolume(d.volume),
					stroke: theme === 'dark' ? '#64748b' : '#9ca3af',
				},
				{
					label: 'Open Price:',
					value: `$${d.open}`,
					stroke: theme === 'dark' ? '#64748b' : '#9ca3af',
				},
				{
					label: 'Close Price:',
					value: `$${d.close}`,
					stroke: theme === 'dark' ? '#64748b' : '#9ca3af',
				},
				{
					label: 'High Price:',
					value: `$${d.high}`,
					stroke: theme === 'dark' ? '#64748b' : '#9ca3af',
				},
				{
					label: 'Low Price:',
					value: `$${d.low}`,
					stroke: theme === 'dark' ? '#64748b' : '#9ca3af',
				},
			],
		};
	};

	const [showModal, setShowModal] = useState(false);
	const [textList, setTextList] = useState([]);

	const handleChoosePosition = (newText) => {
		setTextList((prevTextList) => [...prevTextList, newText]);
	};

	const handleDragComplete = (e, newTextList) => {
		setTextList(newTextList);
	};

	const calculateCenterPosition = () => {
		const plotData = chartRef.current.state.plotData;
		const currentItem = plotData[Math.abs(plotData.length / 2)];
		const averageClose = plotData.reduce((acc, curr) => acc + curr.close, 0) / plotData.length;

		return [xAccessor(currentItem), averageClose];
	};

	const addText = (text) => {
		const newText = {
			text,
			bgFill: theme === 'dark' ? '#f3f4f6' : '#111827',
			bgOpacity: 0.5,
			textFill: theme === 'dark' ? '#000000' : '#ffffff',
			fontSize: 14,
			fontWeight: 'bold',
			position: calculateCenterPosition(),
			selected: false,
		};
		handleChoosePosition(newText);
	};

	return (
		<div>
			<ChartCanvas
				ref={chartRef}
				className={'finquota-chart'}
				height={height}
				ratio={3}
				width={width}
				margin={margin}
				data={data}
				displayXAccessor={displayXAccessor}
				seriesName="Data"
				xScale={xScale}
				xAccessor={xAccessor}
				xExtents={xExtents}
				zoomAnchor={lastVisibleItemBasedZoomAnchor}
				zoomMultiplier={zoom}
			>
				<Chart id={2} height={barChartHeight} origin={barChartOrigin} yExtents={barChartExtents}>
					<BarSeries fillStyle={volumeColor} yAccessor={volumeSeries} />
				</Chart>
				<Chart id={3} height={chartHeight} yExtents={candleChartExtents}>
					<XAxis
						className="finquota-charts-x-axis"
						gridLinesStrokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						showGridLines
						strokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						tickLabelFill={theme === 'dark' ? '#64748b' : '#374151'}
						tickStrokeStyle={theme === 'dark' ? '#64748b' : '#374151'}
						showTickLabel={false}
					/>
					<YAxis
						className="finquota-charts-y-axis"
						showGridLines
						gridLinesStrokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						strokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						tickLabelFill={theme === 'dark' ? '#64748b' : '#374151'}
						tickStrokeStyle={theme === 'dark' ? '#64748b' : '#374151'}
						tickFormat={pricesDisplayFormat}
					/>
					<InteractiveText onDragComplete={handleDragComplete} hoverText={{ text: 'Drag me!' }} textList={textList} enabled={true} />
					{graphType === 'Candle' && <CandlestickSeries />}
					{graphType === 'Line' && <AreaSeries yAccessor={(d) => d.close} />}
					{graphType === 'OHLC' && <OHLCSeries />}
					<LineSeries yAccessor={ema200.accessor()} strokeStyle={ema200.stroke()} />
					<CurrentCoordinate yAccessor={ema200.accessor()} fillStyle={ema200.stroke()} />
					<LineSeries yAccessor={sma50.accessor()} strokeStyle={sma50.stroke()} />
					<CurrentCoordinate yAccessor={sma50.accessor()} fillStyle={sma50.stroke()} />
					{bollingerBandCustom && <BollingerSeries fillStyle={bollingerBandCustom.stroke()} />}
					{bollingerBandCustom && (
						<BollingerBandTooltip
							labelFill={theme === 'dark' ? '#FFFFFF' : '#000000'}
							textFill={theme === 'dark' ? '#FFFFFF' : '#000000'}
							origin={[width - 240, -30]}
							options={bollingerBandCustom.options()}
						/>
					)}

					{range === 'Daily' && technical?.daily?.supports && (
						<>
							<PriceCoordinate
								at="left"
								orient="left"
								strokeDasharray={'ShortDash2'}
								lineStroke={'#16a34a'}
								dx={51}
								strokeWidth={2}
								arrowWidth={7}
								fill={'#16a34a'}
								price={technical?.daily?.supports}
								displayFormat={format('.2f')}
							/>
						</>
					)}

					{range === 'Hourly' && technical?.hourly?.supports && (
						<>
							<PriceCoordinate
								at="left"
								orient="left"
								dx={51}
								strokeDasharray={'ShortDash2'}
								strokeWidth={2}
								arrowWidth={7}
								fill={'#16a34a'}
								lineStroke={'#16a34a'}
								price={technical?.hourly?.supports}
								displayFormat={format('.2f')}
							/>
						</>
					)}

					{range === 'Daily' && technical?.daily?.resistances && (
						<>
							<PriceCoordinate
								at="left"
								orient="left"
								strokeWidth={2}
								strokeDasharray={'ShortDash2'}
								lineStroke={'#ea580c'}
								dx={51}
								arrowWidth={7}
								fill={'#ea580c'}
								price={technical?.daily?.resistances}
								displayFormat={format('.2f')}
							/>
						</>
					)}

					{range === 'Hourly' && technical?.hourly?.resistances && (
						<>
							<PriceCoordinate
								at="left"
								orient="left"
								strokeWidth={2}
								arrowWidth={7}
								fill={'#ea580c'}
								dx={51}
								strokeDasharray={'ShortDash2'}
								lineStroke={'#ea580c'}
								price={technical?.hourly?.resistances}
								displayFormat={format('.2f')}
							/>
						</>
					)}

					{smaCustom && <LineSeries yAccessor={smaCustom.accessor()} strokeStyle={smaCustom.stroke()} />}
					{smaCustom && <CurrentCoordinate yAccessor={smaCustom.accessor()} fillStyle={smaCustom.stroke()} />}
					{emaCustom && <LineSeries yAccessor={emaCustom.accessor()} strokeStyle={emaCustom.stroke()} />}
					{emaCustom && <CurrentCoordinate yAccessor={emaCustom.accessor()} fillStyle={emaCustom.stroke()} />}
					<HoverTooltip
						background={{
							fillStyle: 'transparent',
						}}
						toolTipFillStyle={theme === 'dark' ? 'rgb(31, 41, 55)' : '#0f172a'}
						toolTipStrokeStyle="#151a1e"
						fontFill="#ccc"
						tooltip={{ content: getTooltipContent }}
						yAccessor={(data) => 100000}
					/>
					<MouseCoordinateY rectWidth={margin.right} displayFormat={pricesDisplayFormat} />
					<EdgeIndicator
						itemType="last"
						rectWidth={margin.right}
						fill={openCloseColor}
						lineStroke={openCloseColor}
						displayFormat={pricesDisplayFormat}
						yAccessor={yEdgeIndicator}
					/>
					<MovingAverageTooltip
						className="finquota-charts-tooltip react-financial-charts-moving-average-tooltip"
						labelFill={theme === 'dark' ? '#FFFFFF' : '#000000'}
						textFill={theme === 'dark' ? '#FFFFFF' : '#000000'}
						fontWeight={500}
						displayFormat={format('$,.2f')}
						origin={[0, -30]}
						options={tooltipOptions}
					/>
					{width > 700 && <ZoomButtons />}
					<OHLCTooltip
						fontWeight={500}
						labelFontWeight={500}
						className="finquota-charts-tooltip-hover"
						labelFill={theme === 'dark' ? '#FFFFFF' : '#000000'}
						textFill={theme === 'dark' ? '#FFFFFF' : '#000000'}
						ohlcFormat={format('$,.2f')}
						origin={[0, -35]}
					/>
					{ticker && (
						<Label
							text={ticker || ''}
							fillStyle={theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
							x={(width - margin.left - margin.right) / 2}
							y={(height - margin.top - margin.bottom) / 2}
						/>
					)}
				</Chart>
				<Chart id={4} height={elderRayHeight} yExtents={[0, elder.accessor()]} origin={elderRayOrigin} padding={{ top: 8, bottom: 8 }}>
					<XAxis
						domainClassName="finquota-charts-axis-domain"
						zoomCursorClassName="finquota-charts-ew-resize-cursor"
						className="finquota-charts-x-axis"
						showGridLines
						gridLinesStrokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						strokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						tickLabelFill={theme === 'dark' ? '#64748b' : '#374151'}
						tickStrokeStyle={theme === 'dark' ? '#64748b' : '#374151'}
					/>
					<YAxis
						domainClassName="finquota-charts-axis-domain"
						zoomCursorClassName="finquota-charts-ns-resize-cursor"
						className="finquota-charts-y-axis"
						strokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						tickLabelFill={theme === 'dark' ? '#64748b' : '#374151'}
						tickStrokeStyle={theme === 'dark' ? '#64748b' : '#374151'}
						gridLinesStrokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						ticks={4}
						tickFormat={pricesDisplayFormat}
					/>

					<MouseCoordinateX displayFormat={timeDisplayFormat} />
					<MouseCoordinateY rectWidth={margin.right} displayFormat={pricesDisplayFormat} />

					<ElderRaySeries className="finquota-charts-elderray-series" yAccessor={elder.accessor()} />

					<SingleValueTooltip
						className="finquota-charts-tooltip"
						valueFill={theme === 'dark' ? '#FFFFFF' : '#000000'}
						yAccessor={elder.accessor()}
						labelFill={theme === 'dark' ? '#FFFFFF' : '#4682B4'}
						yLabel="Elder Ray"
						yDisplayFormat={(d) => `${pricesDisplayFormat(d.bullPower)}, ${pricesDisplayFormat(d.bearPower)}`}
						origin={[8, 16]}
					/>
				</Chart>
				{macdCustom && (
					<Chart
						id={5}
						height={macdHeight}
						yExtents={macdCustom.accessor()}
						origin={(w, h) => [0, h - macdHeight - rsiHeight]}
						padding={{ top: 10, bottom: 10 }}
					>
						<XAxis
							axisAt="bottom"
							orient="bottom"
							gridLinesStrokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
							strokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
							tickLabelFill={theme === 'dark' ? '#64748b' : '#374151'}
							tickStrokeStyle={theme === 'dark' ? '#64748b' : '#374151'}
						/>
						<YAxis
							axisAt="right"
							orient="right"
							ticks={2}
							gridLinesStrokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
							strokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
							tickLabelFill={theme === 'dark' ? '#64748b' : '#374151'}
							tickStrokeStyle={theme === 'dark' ? '#64748b' : '#374151'}
						/>

						<MouseCoordinateX at="bottom" orient="bottom" displayFormat={timeFormat('%Y-%m-%d')} rectRadius={5} />
						<MouseCoordinateY at="right" orient="right" displayFormat={format('.2f')} />

						<MACDSeries yAccessor={macdCustom.accessor()} />
						<MACDTooltip
							origin={[0, macdHeight - 10]}
							yAccessor={macdCustom.accessor()}
							options={macdCustom.options()}
							labelFill={theme === 'dark' ? '#ffffff' : '#000000'}
							appearance={{
								strokeStyle: {
									macd: theme === 'dark' ? '#ffffff' : '#000000',
									signal: theme === 'dark' ? '#ffffff' : '#000000',
								},
								fillStyle: {
									divergence: theme === 'dark' ? '#ffffff' : '#000000',
								},
							}}
						/>
					</Chart>
				)}
				{rsiCustom && (
					<Chart id={6} yExtents={[0, 100]} padding={{ top: 10, bottom: 10 }} height={rsiHeight} origin={(w, h) => [0, h - rsiHeight]}>
						<XAxis
							axisAt="bottom"
							orient="bottom"
							gridLinesStrokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
							strokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
							tickLabelFill={theme === 'dark' ? '#64748b' : '#374151'}
							tickStrokeStyle={theme === 'dark' ? '#64748b' : '#374151'}
						/>
						<YAxis
							tickValues={tickValues}
							axisAt="right"
							orient="right"
							ticks={2}
							gridLinesStrokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
							strokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
							tickLabelFill={theme === 'dark' ? '#64748b' : '#374151'}
							tickStrokeStyle={theme === 'dark' ? '#64748b' : '#374151'}
						/>

						<RSISeries yAccessor={rsiCustom.accessor()} overBought={tickValues[0]} overSold={tickValues[2]} middle={tickValues[1]} />

						<RSITooltip
							origin={[8, rsiHeight - 10]}
							labelFill={theme === 'dark' ? '#ffffff' : '#000000'}
							textFill={theme === 'dark' ? '#ffffff' : '#000000'}
							yAccessor={rsiCustom.accessor()}
							options={rsiCustom.options()}
						/>
					</Chart>
				)}
				<CrossHairCursor />
			</ChartCanvas>
			{width !== window.innerWidth - 50 && window.innerWidth > 1024 && (
				<button
					type="button"
					className="inline-flex mt-1 items-center gap-x-2 text-sm font-semibold rounded-lg text-gray-800 hover:text-blue-600 focus:outline-none focus:text-blue-600 disabled:opacity-50 disabled:pointer-events-none dark:text-white dark:hover:text-white/70 dark:focus:text-white/70"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="24"
						height="24"
						onClick={() => {
							document
								.querySelector('.chart-container')
								?.setAttribute('class', 'chart-container md:grid block md:grid-cols-1 lg:grid-cols-1 md:gap-0 lg:gap-6 mt-3');
							setWidth(window.innerWidth - 50);
						}}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						className="lucide lucide-expand"
					>
						<path d="m21 21-6-6m6 6v-4.8m0 4.8h-4.8" />
						<path d="M3 16.2V21m0 0h4.8M3 21l6-6" />
						<path d="M21 7.8V3m0 0h-4.8M21 3l-6 6" />
						<path d="M3 7.8V3m0 0h4.8M3 3l6 6" />
					</svg>
				</button>
			)}
			{width === window.innerWidth - 50 && window.innerWidth > 1024 && (
				<>
					<button
						type="button"
						onClick={() => {
							document
								.querySelector('.chart-container')
								?.setAttribute('class', 'chart-container md:grid block md:grid-cols-1 lg:grid-cols-2 md:gap-0 lg:gap-6 mt-3');
							setWidth(
								window.innerWidth <= 1023
									? (window.innerWidth < maxWidth ? window.innerWidth : maxWidth) - margin.left - margin.right
									: (window.innerWidth / 2 < maxWidth ? window.innerWidth / 2 : maxWidth) - margin.left - margin.right,
							);
						}}
						className="inline-flex mt-1 items-center gap-x-2 text-sm font-semibold rounded-lg text-gray-800 hover:text-blue-600 focus:outline-none focus:text-blue-600 disabled:opacity-50 disabled:pointer-events-none dark:text-white dark:hover:text-white/70 dark:focus:text-white/70"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							className="lucide lucide-shrink"
						>
							<path d="m15 15 6 6m-6-6v4.8m0-4.8h4.8" />
							<path d="M9 19.8V15m0 0H4.2M9 15l-6 6" />
							<path d="M15 4.2V9m0 0h4.8M15 9l6-6" />
							<path d="M9 4.2V9m0 0H4.2M9 9 3 3" />
						</svg>
					</button>
					<button onClick={() => setShowModal(true)} className={'ml-2'}>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<polyline points="4 7 4 4 20 4 20 7" />
							<line x1="9" x2="15" y1="20" y2="20" />
							<line x1="12" x2="12" y1="4" y2="20" />
						</svg>
					</button>
					<ModalInteractiveText showModal={showModal} onClose={() => setShowModal(false)} onSubmit={addText} />
				</>
			)}
			<button onClick={handleDownloadPng} className={'ml-2'}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				>
					<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
					<polyline points="7 10 12 15 17 10" />
					<line x1="12" x2="12" y1="15" y2="3" />
				</svg>
			</button>
		</div>
	);
};

export default StockChart;
